import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react'
import { Checkbox, Dropdown, Input, Modal, Select, Spin, Tooltip, Tree, Typography, message } from 'antd'
import type { DataNode, EventDataNode } from 'antd/es/tree'
import type { MenuProps } from 'antd'
import {
  AppstoreOutlined,
  FolderFilled,
  FolderOpenFilled,
  MoreOutlined,
  PlusOutlined,
  VerticalAlignMiddleOutlined,
} from '@ant-design/icons'
import { adminApi } from '../api'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import { scopeQuery } from '../constants/knowledgeScope'
import type { FolderNode, TreeKey } from './knowledgeFolderUtils'
import {
  DEFAULT_FOLDER_MAX_DEPTH,
  TREE_ROOT_KEY,
  expandKeysForSearchNodes,
  folderIdFromKey,
  getFolderDepth,
  isIngestionInboxFolder,
  parseTreeKey,
} from './knowledgeFolderUtils'
import { KnowledgeFolderSettingsModal } from './KnowledgeFolderSettingsModal'
import './KnowledgeManage.css'

const CREATING_KEY = '__creating__'

function highlightMatch(text: string, keyword: string): ReactNode {
  const kw = keyword.trim()
  if (!kw) return text
  const lowerText = text.toLowerCase()
  const lowerKw = kw.toLowerCase()
  const parts: ReactNode[] = []
  let start = 0
  let idx = lowerText.indexOf(lowerKw, start)
  while (idx !== -1) {
    if (idx > start) parts.push(text.slice(start, idx))
    parts.push(
      <mark key={idx} className="kg-folder-search-mark">
        {text.slice(idx, idx + kw.length)}
      </mark>,
    )
    start = idx + kw.length
    idx = lowerText.indexOf(lowerKw, start)
  }
  if (start < text.length) parts.push(text.slice(start))
  return parts.length ? parts : text
}

type InlineEdit =
  | { mode: 'create'; parentId: number | null; draft: string }
  | { mode: 'rename'; folderId: number; draft: string }

function InlineNameInput({
  value,
  placeholder,
  onChange,
  onConfirm,
  onCancel,
}: {
  value: string
  placeholder: string
  onChange: (v: string) => void
  onConfirm: () => void
  onCancel: () => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  const submittedRef = useRef(false)

  useEffect(() => {
    ref.current?.focus()
    ref.current?.select()
  }, [])

  const handleConfirm = () => {
    if (submittedRef.current) return
    submittedRef.current = true
    onConfirm()
  }

  return (
    <div className="kg-folder-node kg-folder-node--editing" onClick={(e) => e.stopPropagation()}>
      <FolderFilled className="kg-folder-node-icon" />
      <input
        ref={ref}
        className="kg-folder-node-label kg-folder-inline-field"
        type="text"
        value={value}
        placeholder={placeholder}
        maxLength={128}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.stopPropagation()
            handleConfirm()
          }
          if (e.key === 'Escape') {
            e.preventDefault()
            e.stopPropagation()
            onCancel()
          }
        }}
        onBlur={() => {
          if (submittedRef.current) return
          if (!value.trim()) onCancel()
          else window.setTimeout(() => !submittedRef.current && handleConfirm(), 0)
        }}
      />
    </div>
  )
}

function injectCreateNode(nodes: DataNode[], parentId: number | null, createNode: DataNode): DataNode[] {
  if (parentId == null) return [...nodes, createNode]
  const parentKey = `folder-${parentId}`
  return nodes.map((n) => {
    if (n.key === parentKey) {
      return { ...n, children: [...(n.children || []), createNode] }
    }
    if (n.children?.length) {
      return { ...n, children: injectCreateNode(n.children, parentId, createNode) }
    }
    return n
  })
}

function buildTreeFromFlat(nodes: FolderNode[]): DataNode[] {
  const byParent = new Map<number | null, FolderNode[]>()
  for (const f of nodes) {
    const k = f.parent_id
    if (!byParent.has(k)) byParent.set(k, [])
    byParent.get(k)!.push(f)
  }
  const walk = (parentId: number | null): DataNode[] =>
    (byParent.get(parentId) || []).map((f) => ({
      key: `folder-${f.id}`,
      title: f.name,
      isLeaf: !f.has_children,
      children: f.has_children ? walk(f.id) : undefined,
    }))
  return walk(null)
}

function folderToDataNode(
  f: FolderNode,
  childrenMap: Map<number, FolderNode[]>,
  loadedIds: Set<number>,
): DataNode {
  const loaded = loadedIds.has(f.id)
  const kids = childrenMap.get(f.id)
  if (loaded) {
    const childNodes = (kids ?? []).map((c) => folderToDataNode(c, childrenMap, loadedIds))
    return {
      key: `folder-${f.id}`,
      title: f.name,
      isLeaf: childNodes.length === 0,
      children: childNodes.length ? childNodes : undefined,
    }
  }
  if (!f.has_children) {
    return {
      key: `folder-${f.id}`,
      title: f.name,
      isLeaf: true,
    }
  }
  return {
    key: `folder-${f.id}`,
    title: f.name,
    isLeaf: false,
  }
}

type FolderDimension = {
  id: number
  name: string
  is_manual: boolean
  rule_id: number | null
  description?: string
}

async function fetchFolderDimensions(scope: KnowledgeScope): Promise<FolderDimension[]> {
  const r = await adminApi.get<{ items: FolderDimension[] }>('/admin/knowledge/folder-dimensions', {
    params: { ...scopeQuery(scope), tree_only: true },
  })
  return r.data.items || []
}

function defaultDimensionId(items: FolderDimension[]): number | undefined {
  if (!items.length) return undefined
  return (items.find((d) => d.is_manual) ?? items[0]).id
}

async function fetchChildren(
  parentId: number | null,
  scope: KnowledgeScope,
  dimensionId?: number,
): Promise<FolderNode[]> {
  const params = {
    ...scopeQuery(scope),
    ...(parentId != null ? { parent_id: parentId } : {}),
    ...(dimensionId != null ? { dimension_id: dimensionId } : {}),
  }
  const r = await adminApi.get<{ items: FolderNode[] }>('/admin/knowledge/folders/children', {
    params,
  })
  return r.data.items
}

async function fetchFolderItemCounts(
  scope: KnowledgeScope,
  dimensionId?: number,
): Promise<{ counts: Record<string, number>; total: number }> {
  const r = await adminApi.get<{ counts: Record<string, number>; total: number }>(
    '/admin/knowledge/folders/item-counts',
    {
      params: {
        ...scopeQuery(scope),
        ...(dimensionId != null ? { dimension_id: dimensionId } : {}),
      },
    },
  )
  return { counts: r.data.counts || {}, total: r.data.total ?? 0 }
}

function parseFolderItemCounts(raw: Record<string, number>): Map<number, number> {
  const m = new Map<number, number>()
  for (const [k, v] of Object.entries(raw)) {
    const id = Number(k)
    if (Number.isFinite(id) && id > 0) m.set(id, Number(v) || 0)
  }
  return m
}

function resolveFolderParentId(
  id: number,
  folderById: Map<number, FolderNode>,
  roots: FolderNode[],
  childrenMap: Map<number, FolderNode[]>,
): number | null {
  const fromCache = folderById.get(id)
  if (fromCache !== undefined) return fromCache.parent_id
  if (roots.some((r) => r.id === id)) return null
  for (const [parentId, children] of childrenMap) {
    if (children.some((c) => c.id === id)) return parentId
  }
  return null
}

function pruneFoldersFromTreeState(
  ids: Iterable<number>,
  setRoots: Dispatch<SetStateAction<FolderNode[]>>,
  setChildrenMap: Dispatch<SetStateAction<Map<number, FolderNode[]>>>,
  setFolderById: Dispatch<SetStateAction<Map<number, FolderNode>>>,
  setLoadedIds: Dispatch<SetStateAction<Set<number>>>,
  setSearchNodes: Dispatch<SetStateAction<FolderNode[] | null>>,
) {
  const idSet = new Set(ids)
  if (!idSet.size) return
  setRoots((prev) => prev.filter((r) => !idSet.has(r.id)))
  setChildrenMap((prev) => {
    const next = new Map<number, FolderNode[]>()
    for (const [pid, children] of prev) {
      if (idSet.has(pid)) continue
      const filtered = children.filter((c) => !idSet.has(c.id))
      if (filtered.length) next.set(pid, filtered)
    }
    return next
  })
  setFolderById((prev) => {
    const next = new Map(prev)
    for (const id of idSet) next.delete(id)
    return next
  })
  setLoadedIds((prev) => {
    let changed = false
    const next = new Set(prev)
    for (const id of idSet) {
      if (next.delete(id)) changed = true
    }
    return changed ? next : prev
  })
  setSearchNodes((prev) => (prev ? prev.filter((f) => !idSet.has(f.id)) : prev))
}

type Props = {
  knowledgeScope: KnowledgeScope
  treeKey: TreeKey
  onTreeKeyChange: (key: TreeKey) => void
  onStructureChange: () => void
  /** 父级递增时刷新已展开分支（不清空展开状态） */
  treeRefreshToken?: number
  /** 上传完成后额外刷新的文件夹（当前选中目录等） */
  refreshFolderIds?: number[]
  /** 文件夹最大层级，来自后台「切片管理」配置 */
  maxFolderDepth?: number
  /** 虚拟目录维度；不传则使用系统目录 */
  dimensionId?: number
  onDimensionIdChange?: (dimensionId: number) => void
  /** 规则同步维度为只读，不可新建/重命名/删除 */
  readOnly?: boolean
}

export function KnowledgeFolderTree({
  knowledgeScope,
  treeKey,
  onTreeKeyChange,
  onStructureChange,
  treeRefreshToken = 0,
  refreshFolderIds = [],
  maxFolderDepth = DEFAULT_FOLDER_MAX_DEPTH,
  dimensionId,
  onDimensionIdChange,
  readOnly = false,
}: Props) {
  const scopeParams = useMemo(
    () => ({
      ...scopeQuery(knowledgeScope),
      ...(dimensionId != null ? { dimension_id: dimensionId } : {}),
    }),
    [knowledgeScope, dimensionId],
  )
  const [dimensions, setDimensions] = useState<FolderDimension[]>([])
  const [dimensionsLoading, setDimensionsLoading] = useState(false)
  const activeDimension = useMemo(
    () => dimensions.find((d) => d.id === dimensionId),
    [dimensions, dimensionId],
  )
  const dimensionReadOnly = readOnly || Boolean(activeDimension && !activeDimension.is_manual)
  const [inlineEdit, setInlineEdit] = useState<InlineEdit | null>(null)
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [searchNodes, setSearchNodes] = useState<FolderNode[] | null>(null)
  const [searchLoading, setSearchLoading] = useState(false)
  const [roots, setRoots] = useState<FolderNode[]>([])
  const [childrenMap, setChildrenMap] = useState<Map<number, FolderNode[]>>(new Map())
  const [loadedIds, setLoadedIds] = useState<Set<number>>(new Set())
  const [folderById, setFolderById] = useState<Map<number, FolderNode>>(new Map())
  const [rootsLoading, setRootsLoading] = useState(false)
  const [itemCountMap, setItemCountMap] = useState<Map<number, number>>(() => new Map())
  const [totalItemCount, setTotalItemCount] = useState(0)
  const [countsLoading, setCountsLoading] = useState(false)
  const [settingsFolder, setSettingsFolder] = useState<{ id: number; name: string } | null>(null)
  const treeBodyRef = useRef<HTMLDivElement>(null)
  const searchTimerRef = useRef<number | null>(null)
  const loadedIdsRef = useRef(loadedIds)
  loadedIdsRef.current = loadedIds
  const childrenMapRef = useRef(childrenMap)
  childrenMapRef.current = childrenMap
  const folderByIdRef = useRef(folderById)
  folderByIdRef.current = folderById
  const inflightLoadsRef = useRef<Map<number, Promise<void>>>(new Map())
  const refreshFolderIdsRef = useRef(refreshFolderIds)
  refreshFolderIdsRef.current = refreshFolderIds
  const lastTreeRefreshTokenRef = useRef(0)
  const refreshBranchesInFlightRef = useRef<Promise<void> | null>(null)
  const dimensionIdRef = useRef(dimensionId)

  useEffect(() => {
    setDimensionsLoading(true)
    void fetchFolderDimensions(knowledgeScope)
      .then((items) => {
        setDimensions(items)
        if (dimensionId == null) {
          const id = defaultDimensionId(items)
          if (id != null) onDimensionIdChange?.(id)
        } else if (!items.some((d) => d.id === dimensionId)) {
          const id = defaultDimensionId(items)
          if (id != null) onDimensionIdChange?.(id)
        }
      })
      .catch(() => setDimensions([]))
      .finally(() => setDimensionsLoading(false))
  }, [knowledgeScope, dimensionId, onDimensionIdChange])

  useEffect(() => {
    if (dimensionIdRef.current === dimensionId) return
    dimensionIdRef.current = dimensionId
    setRoots([])
    setChildrenMap(new Map())
    setLoadedIds(new Set())
    setFolderById(new Map())
    setExpandedKeys([])
    setSearch('')
    setSearchNodes(null)
    setInlineEdit(null)
    inflightLoadsRef.current.clear()
    setItemCountMap(new Map())
    setTotalItemCount(0)
  }, [dimensionId])

  const loadFolderItemCounts = useCallback(async () => {
    if (dimensionId == null) {
      setItemCountMap(new Map())
      setTotalItemCount(0)
      return
    }
    setCountsLoading(true)
    try {
      const { counts, total } = await fetchFolderItemCounts(knowledgeScope, dimensionId)
      setItemCountMap(parseFolderItemCounts(counts))
      setTotalItemCount(total)
    } catch {
      setItemCountMap(new Map())
      setTotalItemCount(0)
    } finally {
      setCountsLoading(false)
    }
  }, [knowledgeScope, dimensionId])

  const mergeFolders = useCallback((items: FolderNode[]) => {
    setFolderById((prev) => {
      const next = new Map(prev)
      for (const f of items) next.set(f.id, f)
      return next
    })
  }, [])

  const loadRoots = useCallback(async () => {
    setRootsLoading(true)
    try {
      const items = await fetchChildren(null, knowledgeScope, dimensionId)
      setRoots(items)
      mergeFolders(items)
    } catch (e) {
      message.error((e as Error).message)
    } finally {
      setRootsLoading(false)
    }
  }, [mergeFolders, knowledgeScope, dimensionId])

  const syncFolderHasChildren = useCallback((folderId: number, childCount: number) => {
    const hasChildren = childCount > 0
    setFolderById((prev) => {
      const f = prev.get(folderId)
      if (!f || f.has_children === hasChildren) return prev
      const next = new Map(prev)
      next.set(folderId, { ...f, has_children: hasChildren })
      return next
    })
    setRoots((prev) =>
      prev.map((r) => (r.id === folderId ? { ...r, has_children: hasChildren } : r)),
    )
    setChildrenMap((prev) => {
      let changed = false
      const next = new Map(prev)
      for (const [pid, children] of prev) {
        const idx = children.findIndex((c) => c.id === folderId)
        if (idx >= 0 && children[idx].has_children !== hasChildren) {
          const updated = [...children]
          updated[idx] = { ...updated[idx], has_children: hasChildren }
          next.set(pid, updated)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [])

  const applyFolderName = useCallback((folderId: number, name: string) => {
    const rename = (f: FolderNode) => (f.id === folderId ? { ...f, name } : f)
    setFolderById((prev) => {
      const f = prev.get(folderId)
      if (!f || f.name === name) return prev
      const next = new Map(prev)
      next.set(folderId, { ...f, name })
      return next
    })
    setRoots((prev) => prev.map(rename))
    setChildrenMap((prev) => {
      let changed = false
      const next = new Map(prev)
      for (const [pid, children] of prev) {
        const idx = children.findIndex((c) => c.id === folderId)
        if (idx >= 0 && children[idx].name !== name) {
          const updated = [...children]
          updated[idx] = { ...updated[idx], name }
          next.set(pid, updated)
          changed = true
        }
      }
      return changed ? next : prev
    })
    setSearchNodes((prev) => (prev ? prev.map(rename) : prev))
  }, [])

  const applyChildrenLoad = useCallback(
    (parentId: number, items: FolderNode[]) => {
      setChildrenMap((prev) => {
        const next = new Map(prev).set(parentId, items)
        childrenMapRef.current = next
        return next
      })
      setLoadedIds((prev) => {
        const next = new Set(prev).add(parentId)
        loadedIdsRef.current = next
        return next
      })
      mergeFolders(items)
      syncFolderHasChildren(parentId, items.length)
    },
    [mergeFolders, syncFolderHasChildren],
  )

  const fetchChildrenCached = useCallback(
    async (parentId: number, force = false) => {
      if (
        !force &&
        loadedIdsRef.current.has(parentId) &&
        childrenMapRef.current.has(parentId)
      ) {
        return
      }
      const pending = inflightLoadsRef.current.get(parentId)
      if (pending) {
        await pending
        return
      }
      const task = (async () => {
        const items = await fetchChildren(parentId, knowledgeScope, dimensionId)
        applyChildrenLoad(parentId, items)
      })().finally(() => {
        inflightLoadsRef.current.delete(parentId)
      })
      inflightLoadsRef.current.set(parentId, task)
      await task
    },
    [applyChildrenLoad, knowledgeScope, dimensionId],
  )

  const loadChildren = useCallback(
    (parentId: number) => fetchChildrenCached(parentId),
    [fetchChildrenCached],
  )

  const invalidateParent = useCallback(
    async (parentId: number | null) => {
      if (parentId == null) {
        await loadRoots()
        return
      }
      await fetchChildrenCached(parentId, true)
    },
    [fetchChildrenCached, loadRoots],
  )

  useEffect(() => {
    void loadRoots()
  }, [loadRoots])

  useEffect(() => {
    void loadFolderItemCounts()
  }, [loadFolderItemCounts, treeRefreshToken])

  const collectRefreshFolderIds = useCallback((extraFolderIds: number[]) => {
    const ids = new Set<number>()
    const valid = extraFolderIds.filter((fid) => Number.isFinite(fid))
    for (const fid of valid) ids.add(fid)
    // 上传等场景已传入完整路径，无需再沿缓存向上收集祖先
    if (valid.length === 1) {
      let cur = folderByIdRef.current.get(valid[0]!)
      while (cur?.parent_id != null) {
        ids.add(cur.parent_id)
        cur = folderByIdRef.current.get(cur.parent_id)
      }
    }
    return ids
  }, [])

  const refreshFolderBranches = useCallback(
    async (extraFolderIds: number[]) => {
      const ids = collectRefreshFolderIds(extraFolderIds)
      if (!ids.size) return

      const run = async () => {
        const loaded = loadedIdsRef.current
        const pathRoot = extraFolderIds.find((fid) => Number.isFinite(fid))
        const targets = [...ids].filter(
          (fid) => loaded.has(fid) || (pathRoot != null && fid === pathRoot),
        )
        if (!targets.length) return
        await Promise.all(targets.map((fid) => fetchChildrenCached(fid, true)))
      }

      const prev = refreshBranchesInFlightRef.current
      if (prev) await prev.catch(() => undefined)

      const task = run()
      refreshBranchesInFlightRef.current = task
      try {
        await task
      } finally {
        if (refreshBranchesInFlightRef.current === task) {
          refreshBranchesInFlightRef.current = null
        }
      }
    },
    [collectRefreshFolderIds, fetchChildrenCached],
  )

  useEffect(() => {
    if (treeRefreshToken <= 0 || treeRefreshToken === lastTreeRefreshTokenRef.current) return
    lastTreeRefreshTokenRef.current = treeRefreshToken
    void refreshFolderBranches(refreshFolderIdsRef.current)
  }, [treeRefreshToken, refreshFolderBranches])

  useEffect(() => {
    if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current)
    const kw = search.trim()
    if (!kw) {
      setSearchNodes(null)
      setSearchLoading(false)
      return
    }
    setSearchLoading(true)
    searchTimerRef.current = window.setTimeout(() => {
      adminApi
        .get<{ items: FolderNode[] }>('/admin/knowledge/folders/search', {
          params: { q: kw, ...scopeParams },
        })
        .then((r) => {
          setSearchNodes(r.data.items)
          mergeFolders(r.data.items)
          setExpandedKeys(expandKeysForSearchNodes(r.data.items))
        })
        .catch((e) => message.error(e.message))
        .finally(() => setSearchLoading(false))
    }, 300)
    return () => {
      if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current)
    }
  }, [search, mergeFolders, knowledgeScope])

  const searchKeyword = search.trim()
  const createParentId = useMemo(() => folderIdFromKey(treeKey), [treeKey])
  const createParentName =
    createParentId != null ? folderById.get(createParentId)?.name : null
  const createParentDepth =
    createParentId != null ? getFolderDepth(createParentId, folderById) : 0
  const createParentFolder =
    createParentId != null ? folderById.get(createParentId) : undefined
  const createParentIsIngestionInbox = createParentFolder
    ? isIngestionInboxFolder(createParentFolder)
    : false
  const canCreateChild =
    !createParentIsIngestionInbox &&
    (createParentId == null || createParentDepth < maxFolderDepth)

  const startCreate = useCallback(
    (parentId: number | null) => {
      if (inlineEdit) return
      if (parentId != null) {
        const parent = folderById.get(parentId)
        if (parent && isIngestionInboxFolder(parent)) {
          message.warning('「采集文件夹」为系统暂存目录，不可创建子文件夹')
          return
        }
      }
      if (parentId != null && getFolderDepth(parentId, folderById) >= maxFolderDepth) {
        message.warning(`文件夹层级最多 ${maxFolderDepth} 级`)
        return
      }
      setInlineEdit({ mode: 'create', parentId, draft: '' })
      if (parentId != null) {
        void loadChildren(parentId).then(() => {
          setExpandedKeys((prev) => [...new Set([...prev, `folder-${parentId}`])])
        })
      }
    },
    [inlineEdit, folderById, loadChildren],
  )

  const startRename = useCallback(
    (folder: FolderNode) => {
      if (inlineEdit) return
      setInlineEdit({ mode: 'rename', folderId: folder.id, draft: folder.name })
    },
    [inlineEdit],
  )

  const cancelInline = useCallback(() => setInlineEdit(null), [])

  const submitCreate = useCallback(() => {
    if (inlineEdit?.mode !== 'create') return
    const name = inlineEdit.draft.trim()
    const parentId = inlineEdit.parentId
    if (!name) {
      cancelInline()
      return
    }
    adminApi
      .post<{ id: number }>('/admin/knowledge/folders', { name, parent_id: parentId }, {
        params: scopeParams,
      })
      .then(async (res) => {
        const newId = res.data.id
        const newFolder: FolderNode = {
          id: newId,
          name,
          parent_id: parentId,
          sort_order: 0,
          has_children: false,
        }
        mergeFolders([newFolder])
        if (parentId != null) {
          setChildrenMap((prev) => {
            const kids = prev.get(parentId) ?? []
            if (kids.some((c) => c.id === newId)) return prev
            const nextKids = [...kids, newFolder]
            syncFolderHasChildren(parentId, nextKids.length)
            const next = new Map(prev)
            next.set(parentId, nextKids)
            return next
          })
          setLoadedIds((prev) => new Set(prev).add(parentId))
        } else {
          setRoots((prev) => (prev.some((r) => r.id === newId) ? prev : [...prev, newFolder]))
        }
        cancelInline()
        await invalidateParent(parentId)
        onStructureChange()
        void loadFolderItemCounts()
        onTreeKeyChange(`folder-${newId}`)
        setExpandedKeys((prev) => {
          const keys: string[] = []
          if (parentId != null) keys.push(`folder-${parentId}`)
          return [...new Set([...prev, ...keys])]
        })
        message.success(`已创建「${name}」`)
        if (search.trim()) {
          const r = await adminApi.get<{ items: FolderNode[] }>(
            '/admin/knowledge/folders/search',
            { params: { q: search.trim(), ...scopeParams } },
          )
          setSearchNodes(r.data.items)
          mergeFolders(r.data.items)
          setExpandedKeys(expandKeysForSearchNodes(r.data.items))
        }
      })
      .catch((e) => message.error(e.message))
  }, [
    inlineEdit,
    cancelInline,
    invalidateParent,
    onStructureChange,
    onTreeKeyChange,
    search,
    knowledgeScope,
    mergeFolders,
    syncFolderHasChildren,
    loadFolderItemCounts,
  ])

  const submitRename = useCallback(() => {
    if (inlineEdit?.mode !== 'rename') return
    const name = inlineEdit.draft.trim()
    if (!name) {
      cancelInline()
      return
    }
    const folder = folderById.get(inlineEdit.folderId)
    if (folder && folder.name === name) {
      cancelInline()
      return
    }
    adminApi
      .put(`/admin/knowledge/folders/${inlineEdit.folderId}`, { name }, {
        params: scopeParams,
      })
      .then(() => {
        message.success('已重命名')
        applyFolderName(inlineEdit.folderId, name)
        cancelInline()
        void invalidateParent(folder?.parent_id ?? null)
        onStructureChange()
        void loadFolderItemCounts()
      })
      .catch((e) => message.error(e.message))
  }, [
    inlineEdit,
    folderById,
    cancelInline,
    invalidateParent,
    onStructureChange,
    knowledgeScope,
    applyFolderName,
    loadFolderItemCounts,
  ])

  const refreshSearchIfNeeded = useCallback(async () => {
    const kw = search.trim()
    if (!kw) return
    const r = await adminApi.get<{ items: FolderNode[] }>('/admin/knowledge/folders/search', {
      params: { q: kw, ...scopeParams },
    })
    setSearchNodes(r.data.items)
    mergeFolders(r.data.items)
    setExpandedKeys(expandKeysForSearchNodes(r.data.items))
  }, [search, knowledgeScope, mergeFolders])

  const deleteFolder = useCallback(
    (id: number, cascadeChildren: boolean) => {
      const parentId = resolveFolderParentId(id, folderById, roots, childrenMap)
      return adminApi
        .delete(`/admin/knowledge/folders/${id}`, {
          params: { ...scopeParams, cascade_children: cascadeChildren },
        })
        .then(async (r) => {
          const deletedIds =
            (r.data as { deleted_folder_ids?: number[] }).deleted_folder_ids ?? [id]
          const deletedIdSet = new Set(deletedIds)
          const n = deletedIds.length
          message.success(n > 1 ? `已删除文件夹（含 ${n} 个目录）` : '已删除')
          const deletedKeys = new Set(deletedIds.map((fid) => `folder-${fid}`))
          const selectedFid = folderIdFromKey(treeKey)
          if (
            deletedKeys.has(treeKey) ||
            (selectedFid != null && deletedIdSet.has(selectedFid))
          ) {
            onTreeKeyChange(TREE_ROOT_KEY)
          }
          setExpandedKeys((prev) => prev.filter((k) => !deletedKeys.has(k)))
          pruneFoldersFromTreeState(
            deletedIds,
            setRoots,
            setChildrenMap,
            setFolderById,
            setLoadedIds,
            setSearchNodes,
          )
          await invalidateParent(parentId)
          await refreshSearchIfNeeded()
          onStructureChange()
          void loadFolderItemCounts()
        })
        .catch((e) => {
          message.error(e.message)
          throw e
        })
    },
    [
      folderById,
      roots,
      childrenMap,
      treeKey,
      onTreeKeyChange,
      invalidateParent,
      onStructureChange,
      knowledgeScope,
      refreshSearchIfNeeded,
      loadFolderItemCounts,
    ],
  )

  const confirmDeleteFolder = useCallback(
    (folder: FolderNode, loadedChildren: FolderNode[] | undefined) => {
      const hasChildFolders = Boolean(folder.has_children || loadedChildren?.length)
      const cascadeRef = { current: hasChildFolders }

      Modal.confirm({
        title: `删除「${folder.name}」？`,
        okText: hasChildFolders ? '确认删除' : '删除',
        cancelText: '取消',
        okType: 'danger',
        content: hasChildFolders ? (
          <div>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
              仅当本文件夹及子文件夹内均无知识文档时可删除。默认将一并删除所有子文件夹。
            </Typography.Paragraph>
            <Checkbox
              defaultChecked
              onChange={(e) => {
                cascadeRef.current = e.target.checked
              }}
            >
              同时删除所有子文件夹
            </Checkbox>
          </div>
        ) : (
          <Typography.Text type="secondary">
            仅当文件夹内无知识文档时可删除；删除后不可恢复。
          </Typography.Text>
        ),
        onOk: () => deleteFolder(folder.id, cascadeRef.current),
      })
    },
    [deleteFolder],
  )

  useEffect(() => {
    setInlineEdit(null)
  }, [knowledgeScope])

  const createNode: DataNode = useMemo(
    () => ({ key: CREATING_KEY, title: '', isLeaf: true, selectable: false }),
    [],
  )

  const baseTree: DataNode[] = useMemo(() => {
    let folderNodes: DataNode[]
    if (searchNodes) {
      folderNodes = buildTreeFromFlat(searchNodes)
    } else {
      folderNodes = roots.map((f) => folderToDataNode(f, childrenMap, loadedIds))
    }
    if (inlineEdit?.mode === 'create' && !searchNodes) {
      folderNodes = injectCreateNode(folderNodes, inlineEdit.parentId, createNode)
    }
    return folderNodes
  }, [roots, childrenMap, loadedIds, searchNodes, inlineEdit, createNode])

  const onLoadData = useCallback(
    (node: EventDataNode<DataNode>) => {
      const key = String(node.key)
      if (!key.startsWith('folder-')) return Promise.resolve()
      const fid = Number(key.slice(7))
      return loadChildren(fid).then(() => undefined)
    },
    [loadChildren],
  )

  const titleRender = useCallback(
    (node: DataNode) => {
      const key = String(node.key)

      if (key === CREATING_KEY && inlineEdit?.mode === 'create') {
        return (
          <InlineNameInput
            value={inlineEdit.draft}
            placeholder="输入名称"
            onChange={(draft) => setInlineEdit({ ...inlineEdit, draft })}
            onConfirm={submitCreate}
            onCancel={cancelInline}
          />
        )
      }

      if (key.startsWith('folder-')) {
        const fid = Number(key.slice(7))
        const folder = folderById.get(fid)
        if (!folder) return <span>{node.title as string}</span>

        const depth = getFolderDepth(fid, folderById)
        const atMaxDepth = depth >= maxFolderDepth
        const folderIsIngestionInbox = isIngestionInboxFolder(folder)
        const folderStructureReadOnly = dimensionReadOnly || folderIsIngestionInbox

        if (inlineEdit?.mode === 'rename' && inlineEdit.folderId === fid) {
          return (
            <InlineNameInput
              value={inlineEdit.draft}
              placeholder="文件夹名称"
              onChange={(draft) => setInlineEdit({ ...inlineEdit, draft })}
              onConfirm={submitRename}
              onCancel={cancelInline}
            />
          )
        }

        const menuItems: MenuProps['items'] = folderIsIngestionInbox
          ? []
          : [
          {
            key: 'rename',
            label: '重命名',
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation()
              startRename(folder)
            },
          },
          {
            key: 'child',
            label: atMaxDepth ? `已达 ${maxFolderDepth} 级上限` : '新建子文件夹',
            disabled: atMaxDepth,
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation()
              if (!atMaxDepth) startCreate(fid)
            },
          },
          {
            key: 'settings',
            label: '目录设置',
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation()
              setSettingsFolder({ id: fid, name: folder.name })
            },
          },
          { type: 'divider' },
          {
            key: 'delete',
            label: '删除',
            danger: true,
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation()
              confirmDeleteFolder(folder, childrenMap.get(fid))
            },
          },
        ]

        const isExpanded = expandedKeys.includes(key)
        const FolderIcon = isExpanded ? FolderOpenFilled : FolderFilled

        return (
          <div
            className="kg-folder-node"
            onDoubleClick={
              folderStructureReadOnly
                ? undefined
                : (e) => {
                    e.stopPropagation()
                    startRename(folder)
                  }
            }
          >
            <FolderIcon className="kg-folder-node-icon" />
            <span className="kg-folder-node-label" title={folder.name}>
              {searchKeyword && searchNodes
                ? highlightMatch(folder.name, searchKeyword)
                : folder.name}
            </span>
            <span className="kg-folder-node-count" aria-hidden="true">
              {itemCountMap.get(fid) ?? 0}
            </span>
            <span
              className={`kg-folder-node-actions${folderStructureReadOnly ? ' kg-folder-node-actions--placeholder' : ''}`}
              aria-hidden={folderStructureReadOnly || undefined}
              onClick={folderStructureReadOnly ? undefined : (e) => e.stopPropagation()}
            >
              {!folderStructureReadOnly ? (
                <>
                  <Tooltip title={atMaxDepth ? `已达 ${maxFolderDepth} 级上限` : '新建子文件夹'}>
                    <button
                      type="button"
                      className="kg-folder-action-btn"
                      disabled={atMaxDepth}
                      onClick={() => !atMaxDepth && startCreate(fid)}
                    >
                      <PlusOutlined />
                    </button>
                  </Tooltip>
                  <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                    <button type="button" className="kg-folder-action-btn" aria-label="更多">
                      <MoreOutlined />
                    </button>
                  </Dropdown>
                </>
              ) : null}
            </span>
          </div>
        )
      }

      return null
    },
    [
      inlineEdit,
      folderById,
      childrenMap,
      expandedKeys,
      searchKeyword,
      searchNodes,
      submitCreate,
      submitRename,
      cancelInline,
      startRename,
      startCreate,
      confirmDeleteFolder,
      dimensionReadOnly,
      itemCountMap,
      maxFolderDepth,
    ],
  )

  const onSelect = (keys: React.Key[]) => {
    if (!keys.length || keys[0] === CREATING_KEY) return
    if (inlineEdit) return
    onTreeKeyChange(parseTreeKey(String(keys[0])))
  }

  const addTooltip = createParentIsIngestionInbox
    ? '「采集文件夹」为系统暂存目录，不可创建子文件夹'
    : !canCreateChild
      ? `已达 ${maxFolderDepth} 级上限`
      : createParentName
        ? `在「${createParentName}」下新建子文件夹`
        : '新建文件夹（根级）'

  return (
    <div className="kg-folder-tree">
      {dimensions.length > 1 ? (
        <div className="kg-folder-dimension-bar">
          <Typography.Text type="secondary" className="kg-folder-dimension-label">
            分类类型
          </Typography.Text>
          <Select
            size="small"
            loading={dimensionsLoading}
            value={dimensionId}
            placeholder="选择分类类型"
            options={dimensions.map((d) => ({
              value: d.id,
              label: d.is_manual ? '系统目录' : d.name,
            }))}
            onChange={(id) => {
              if (id != null) onDimensionIdChange?.(id)
            }}
            className="kg-folder-dimension-select"
            popupClassName="kg-folder-dimension-select-popup"
          />
        </div>
      ) : null}
      <div className="kg-folder-head">
        <span className="kg-folder-head-title">目录</span>
        <span className="kg-folder-head-actions">
          <Tooltip title="全部折叠">
            <button
              type="button"
              className="kg-folder-head-add"
              aria-label="全部折叠"
              onClick={() => setExpandedKeys([])}
            >
              <VerticalAlignMiddleOutlined />
            </button>
          </Tooltip>
          {!dimensionReadOnly ? (
            <Tooltip title={addTooltip}>
              <button
                type="button"
                className="kg-folder-head-add"
                disabled={!canCreateChild}
                onClick={() => canCreateChild && startCreate(createParentId)}
              >
                <PlusOutlined />
              </button>
            </Tooltip>
          ) : null}
        </span>
      </div>
      <Input
        allowClear
        size="small"
        placeholder="搜索文件夹"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="kg-folder-search"
      />
      <button
        type="button"
        className={`kg-folder-all-item${treeKey === TREE_ROOT_KEY ? ' kg-folder-all-item--active' : ''}`}
        onClick={() => onTreeKeyChange(TREE_ROOT_KEY)}
      >
        <AppstoreOutlined className="kg-folder-all-item-icon" />
        <span className="kg-folder-all-item-label">全部知识点</span>
        <span className="kg-folder-all-item-count">{countsLoading ? '…' : totalItemCount}</span>
      </button>
      <div ref={treeBodyRef} className="kg-folder-tree-body">
        <Spin spinning={rootsLoading || searchLoading} size="small" wrapperClassName="kg-folder-tree-spin">
          <div className="kg-folder-tree-scroll">
            <Tree
              className="kg-folder-ant-tree"
              showIcon={false}
              blockNode
              expandAction={false}
              loadData={searchNodes ? undefined : onLoadData}
              selectedKeys={
                inlineEdit?.mode === 'create' || treeKey === TREE_ROOT_KEY ? [] : [treeKey]
              }
              expandedKeys={expandedKeys}
              onExpand={(keys) => {
                setExpandedKeys(keys as string[])
              }}
              treeData={baseTree}
              titleRender={titleRender}
              onSelect={onSelect}
            />
          </div>
        </Spin>
      </div>
      <KnowledgeFolderSettingsModal
        open={settingsFolder != null}
        folderId={settingsFolder?.id ?? null}
        folderName={settingsFolder?.name ?? ''}
        scopeParams={scopeParams}
        onClose={() => setSettingsFolder(null)}
        onSaved={onStructureChange}
      />
    </div>
  )
}
